/*
  Shared storefront enhancement script.
  The theme remains functional without JavaScript, and this file only improves
  the header search panel and mobile drawer interactions.
*/
document.documentElement.classList.remove('no-js');

/*
  The shared cart feedback helper keeps Ajax add-to-cart, badge refresh, and
  toast rendering in one place so homepage rails and collection browsers do not
  drift apart over time.
*/
(function () {
  var toastHideTimer = null;
  var pendingVariantAdds = {};

  function getToastRoot() {
    return document.querySelector('[data-cart-toast-root]');
  }

  function getToastParts() {
    var toastRoot = getToastRoot();

    if (!toastRoot) {
      return {};
    }

    return {
      root: toastRoot,
      /*
        The toast root and the toast target intentionally live on the same DOM
        element. querySelector() only searches descendants, so we must accept
        the root itself before falling back to a nested match.
      */
      toast: toastRoot.matches('[data-cart-toast]') ? toastRoot : toastRoot.querySelector('[data-cart-toast]'),
      title: toastRoot.querySelector('[data-cart-toast-title]'),
      product: toastRoot.querySelector('[data-cart-toast-product]'),
      variant: toastRoot.querySelector('[data-cart-toast-variant]'),
      actions: toastRoot.querySelector('[data-cart-toast-actions]')
    };
  }

  function buildCartErrorMessage(payload, fallbackMessage) {
    if (!payload) {
      return fallbackMessage || '';
    }

    if (typeof payload.description === 'string' && payload.description !== '') {
      return payload.description;
    }

    if (typeof payload.message === 'string' && payload.message !== '') {
      return payload.message;
    }

    return fallbackMessage || '';
  }

  function readPositiveInteger(value) {
    var parsedValue = Number(value);

    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
      return null;
    }

    return Math.floor(parsedValue);
  }

  function readStockLimitMessage(kind) {
    var toastRoot = getToastRoot();

    if (!toastRoot) {
      return '';
    }

    if (kind === 'at-max') {
      return toastRoot.getAttribute('data-toast-stock-limit-at-max') || '';
    }

    return toastRoot.getAttribute('data-toast-stock-limit-exceeded') || '';
  }

  function createCartError(message) {
    var cartError = new Error(message || '');

    cartError.userMessage = message || '';

    return cartError;
  }

  function createStockLimitError(existingQuantity, maxQuantity) {
    var messageKind = existingQuantity >= maxQuantity ? 'at-max' : 'exceeded';
    var stockLimitError = createCartError(readStockLimitMessage(messageKind));

    stockLimitError.isStockLimit = true;

    return stockLimitError;
  }

  function isInventoryErrorMessage(message) {
    var normalizedMessage = String(message || '').toLowerCase();

    return (
      normalizedMessage.indexOf('inventory') !== -1 ||
      normalizedMessage.indexOf('stock') !== -1 ||
      normalizedMessage.indexOf('available') !== -1 ||
      normalizedMessage.indexOf('quantity') !== -1 ||
      normalizedMessage.indexOf('lager') !== -1 ||
      normalizedMessage.indexOf('verfügbar') !== -1 ||
      normalizedMessage.indexOf('verfuegbar') !== -1 ||
      normalizedMessage.indexOf('menge') !== -1
    );
  }

  function buildUserCartError(payload, fallbackMessage) {
    var rawMessage = buildCartErrorMessage(payload, fallbackMessage);
    var inventoryMessage = readStockLimitMessage('exceeded');

    if (inventoryMessage && isInventoryErrorMessage(rawMessage)) {
      return inventoryMessage;
    }

    return rawMessage;
  }

  function fetchCartState() {
    var toastRoot = getToastRoot();
    var cartStateUrl = toastRoot ? toastRoot.getAttribute('data-cart-state-url') : '';

    if (!cartStateUrl) {
      return Promise.resolve(null);
    }

    return fetch(cartStateUrl, {
      headers: {
        Accept: 'application/json'
      }
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('cart-state');
        }

        return response.json();
      })
      .then(function (cartState) {
        return cartState;
      });
  }

  function refreshCartCount() {
    return fetchCartState()
      .then(function (cartState) {
        var nextItemCount = cartState && typeof cartState.item_count === 'number' ? cartState.item_count : 0;

        document.querySelectorAll('.site-cart-badge, [data-cart-count]').forEach(function (countElement) {
          countElement.textContent = String(nextItemCount);
        });

        return cartState;
      });
  }

  function getVariantQuantityInCart(cartState, variantId) {
    var normalizedVariantId = Number(variantId);

    if (!cartState || !Array.isArray(cartState.items) || !Number.isFinite(normalizedVariantId)) {
      return 0;
    }

    /*
      Shopify can split the same variant into several cart lines when line item
      properties differ. Inventory belongs to the variant, so sum every match.
    */
    return cartState.items.reduce(function (runningTotal, cartItem) {
      if (Number(cartItem && cartItem.variant_id) !== normalizedVariantId) {
        return runningTotal;
      }

      return runningTotal + (Number(cartItem.quantity) || 0);
    }, 0);
  }

  function preflightVariantStockLimit(requestBody, maxQuantity) {
    var finiteMaxQuantity = readPositiveInteger(maxQuantity);

    if (finiteMaxQuantity === null) {
      return Promise.resolve();
    }

    return fetchCartState().then(function (cartState) {
      var existingQuantity = getVariantQuantityInCart(cartState, requestBody.id);
      var requestedQuantity = readPositiveInteger(requestBody.quantity) || 1;

      if (existingQuantity + requestedQuantity > finiteMaxQuantity) {
        throw createStockLimitError(existingQuantity, finiteMaxQuantity);
      }
    });
  }

  /*
    One low-level cart/add helper keeps single-item and bulk Wishlist adds on
    the exact same Ajax endpoint and error-shaping path.
  */
  function postCartAdd(requestBody) {
    var toastRoot = getToastRoot();
    var cartAddUrl = toastRoot ? toastRoot.getAttribute('data-cart-add-url') : '';
    var fallbackError = toastRoot ? toastRoot.getAttribute('data-toast-error-title') : '';

    if (!cartAddUrl) {
      return Promise.reject(new Error(fallbackError || ''));
    }

    return fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(requestBody)
    }).then(function (response) {
      return response.json().then(function (payload) {
        if (!response.ok) {
          throw createCartError(buildUserCartError(payload, fallbackError));
        }

        return payload;
      });
    });
  }

  function closeCartToast() {
    var toastParts = getToastParts();

    if (!toastParts.toast) {
      return;
    }

    window.clearTimeout(toastHideTimer);
    toastParts.toast.classList.remove('is-visible', 'is-error');
    toastParts.toast.hidden = true;
  }

  function showCartToast(options) {
    var toastParts = getToastParts();
    var toastRoot = toastParts.root;
    var toastTitle = toastRoot ? toastRoot.getAttribute('data-toast-success-title') : '';
    var errorTitle = toastRoot ? toastRoot.getAttribute('data-toast-error-title') : '';
    var hasActions = !(options && options.showActions === false);

    if (!toastParts.toast || !toastParts.title || !toastParts.product || !toastParts.variant || !toastParts.actions) {
      return;
    }

    /*
      With the cart drawer on the page, a successful add opens the drawer
      (component-cart.js listens for tcghive:cart:updated), so the success
      toast would only duplicate it. Errors still use the toast.
    */
    if (options && options.type === 'success' && document.querySelector('[data-cart-drawer]')) {
      return;
    }

    toastParts.toast.classList.toggle('is-error', options && options.type === 'error');
    toastParts.title.textContent =
      (options && options.title) || (options && options.type === 'error' ? errorTitle : toastTitle) || '';
    toastParts.product.textContent = (options && options.productTitle) || '';
    toastParts.variant.textContent = (options && options.variantSummary) || '';
    toastParts.variant.hidden = !((options && options.variantSummary) || '');
    toastParts.actions.hidden = !hasActions;
    toastParts.toast.hidden = false;

    window.requestAnimationFrame(function () {
      toastParts.toast.classList.add('is-visible');
    });
  }

  function addVariantToCart(options) {
    var requestBody = {
      id: Number(options.variantId),
      quantity: Number(options.quantity || 1)
    };
    var variantLockKey;
    var addRequest;

    if (!requestBody.id) {
      return Promise.reject(new Error(''));
    }

    requestBody.quantity = readPositiveInteger(requestBody.quantity) || 1;
    variantLockKey = String(requestBody.id);

    if (pendingVariantAdds[variantLockKey]) {
      return Promise.reject(
        createCartError(getToastRoot() ? getToastRoot().getAttribute('data-toast-error-title') || '' : '')
      );
    }

    addRequest = preflightVariantStockLimit(requestBody, options.maxQuantity)
      .then(function () {
        return postCartAdd(requestBody);
      })
      .then(function () {
        return refreshCartCount();
      })
      .then(function (cartState) {
        /*
          Broadcast one shared success event after Shopify confirms the add.
          Pages like /cart can listen for this and refresh their server-rendered
          markup without reimplementing add-to-cart logic a second time.
        */
        document.dispatchEvent(
          new CustomEvent('tcghive:cart:updated', {
            detail: {
              source: 'shared-add-to-cart',
              variantId: requestBody.id,
              quantity: requestBody.quantity,
              cart: cartState || null
            }
          })
        );

        return cartState;
      })
      .finally(function () {
        delete pendingVariantAdds[variantLockKey];
      });

    pendingVariantAdds[variantLockKey] = addRequest;

    return addRequest;
  }

  /*
    Bulk Wishlist add uses the same shared cart service but reports each line
    item result so the page can show one coherent partial-success message.
  */
  function addItemsToCart(items) {
    var normalizedItems = Array.isArray(items)
      ? items
          .map(function (item) {
            return {
              id: Number(item && item.id),
              quantity: Number(item && item.quantity ? item.quantity : 1),
              maxQuantity: readPositiveInteger(item && item.maxQuantity)
            };
          })
          .filter(function (item) {
            return Number.isFinite(item.id) && item.id > 0 && Number.isFinite(item.quantity) && item.quantity > 0;
          })
      : [];
    var addedItems = [];
    var failedItems = [];
    var requestQueue = Promise.resolve();

    if (!normalizedItems.length) {
      return Promise.reject(new Error(''));
    }

    normalizedItems.forEach(function (item) {
      requestQueue = requestQueue.then(function () {
        var requestBody = {
          id: item.id,
          quantity: readPositiveInteger(item.quantity) || 1
        };
        var variantLockKey = String(requestBody.id);

        if (pendingVariantAdds[variantLockKey]) {
          failedItems.push({
            id: item.id,
            quantity: item.quantity,
            error: createCartError(getToastRoot() ? getToastRoot().getAttribute('data-toast-error-title') || '' : '')
          });
          return Promise.resolve();
        }

        pendingVariantAdds[variantLockKey] = preflightVariantStockLimit(requestBody, item.maxQuantity)
          .then(function () {
            return postCartAdd(requestBody);
          });

        return pendingVariantAdds[variantLockKey]
          .then(function (payload) {
            addedItems.push({
              id: item.id,
              quantity: requestBody.quantity,
              payload: payload
            });
          })
          .catch(function (error) {
            failedItems.push({
              id: item.id,
              quantity: requestBody.quantity,
              error: error
            });
          })
          .finally(function () {
            delete pendingVariantAdds[variantLockKey];
          });
      });
    });

    return requestQueue.then(function () {
      if (!addedItems.length && failedItems.length) {
        throw failedItems[0].error;
      }

      return refreshCartCount().then(function (cartState) {
        document.dispatchEvent(
          new CustomEvent('tcghive:cart:updated', {
            detail: {
              source: 'shared-add-many-to-cart',
              items: normalizedItems,
              cart: cartState || null
            }
          })
        );

        return {
          addedItems: addedItems,
          failedItems: failedItems,
          cart: cartState || null
        };
      });
    });
  }

  window.TCGHiveCartFeedback = {
    addItemsToCart: addItemsToCart,
    addVariantToCart: addVariantToCart,
    closeCartToast: closeCartToast,
    getCartState: fetchCartState,
    refreshCartCount: refreshCartCount,
    showCartToast: showCartToast
  };
})();

document.addEventListener('DOMContentLoaded', function () {
  var headerRoot = document.querySelector('[data-tcghive-header]');
  var rootStyleTarget = document.documentElement;
  var bodyElement = document.body;
  var sharedCartToastCloseButton = document.querySelector('[data-cart-toast-close]');
  /*
    The header has three intentional states:
    - mobile / small tablet below 900px
    - compact desktop from 900px upward
    - full desktop sizing layered on later CSS breakpoints
    The search panel should therefore stay available in the compact desktop
    range instead of disappearing until 1200px.
  */
  var desktopHeaderBreakpoint = window.matchMedia('(min-width: 900px)');

  function setShellMeasurements() {
    var announcementBar = document.querySelector('.site-announcement');
    var headerShell = document.querySelector('.tcghive-header-shell');
    var headerBar = headerShell ? headerShell.querySelector('.site-header') : null;
    var mobileSearchBar = headerShell ? headerShell.querySelector('.site-header__mobile-search') : null;
    var announcementHeight = announcementBar ? announcementBar.offsetHeight : 0;
    var desktopHeaderHeight = headerBar ? headerBar.offsetHeight : 0;
    var mobileSearchHeight = mobileSearchBar ? mobileSearchBar.offsetHeight : 0;

    /*
      These CSS variables keep the fixed header stack aligned with the actual
      rendered heights instead of relying on hard-coded offsets.
    */
    rootStyleTarget.style.setProperty('--tcghive-announcement-height', announcementHeight + 'px');
    rootStyleTarget.style.setProperty('--tcghive-shell-offset-desktop', desktopHeaderHeight + 'px');
    rootStyleTarget.style.setProperty(
      '--tcghive-shell-offset-mobile',
      desktopHeaderHeight + mobileSearchHeight + 'px'
    );

    if (bodyElement) {
      if (announcementHeight > 0) {
        bodyElement.setAttribute('data-announcement-active', 'true');
      } else {
        bodyElement.removeAttribute('data-announcement-active');
      }
    }
  }

  function scheduleShellMeasurement() {
    window.requestAnimationFrame(function () {
      setShellMeasurements();
    });
  }

  function initializeFaqAccordions(scope) {
    var faqRoots = (scope || document).querySelectorAll('[data-tcghive-faq]');

    faqRoots.forEach(function (faqRoot) {
      var faqItems;

      if (faqRoot.getAttribute('data-faq-initialized') === 'true') {
        return;
      }

      faqRoot.setAttribute('data-faq-initialized', 'true');
      faqItems = Array.prototype.slice.call(faqRoot.querySelectorAll('[data-faq-item]'));

      if (!faqItems.length) {
        return;
      }

      function isFaqItemOpen(faqItem) {
        return faqItem.classList.contains('is-active');
      }

      function setFaqItemState(faqItem, shouldOpen) {
        var faqTrigger = faqItem.querySelector('[data-faq-trigger]');
        var faqContent = faqItem.querySelector('[data-faq-content]');

        if (!faqTrigger || !faqContent) {
          return;
        }

        faqItem.classList.toggle('is-active', shouldOpen);
        faqTrigger.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
        faqContent.hidden = !shouldOpen;
      }

      function setAllFaqItemsState(shouldOpen) {
        faqItems.forEach(function (faqItem) {
          setFaqItemState(faqItem, shouldOpen);
        });
      }

      faqItems.forEach(function (faqItem) {
        var faqTrigger = faqItem.querySelector('[data-faq-trigger]');

        /*
          Start every FAQ closed so the first row can act as the master
          controller and the remaining rows still behave like normal accordions.
        */
        setFaqItemState(faqItem, false);

        if (!faqTrigger) {
          return;
        }

        faqTrigger.addEventListener('click', function () {
          if (faqItem.hasAttribute('data-faq-master')) {
            /*
              The first question opens or closes the entire FAQ group to match
              the approved storefront interaction.
            */
            setAllFaqItemsState(!faqItems.every(isFaqItemOpen));
            return;
          }

          setFaqItemState(faqItem, !isFaqItemOpen(faqItem));
        });
      });
    });
  }

  if (headerRoot) {
    var mobileOpenButtons = headerRoot.querySelectorAll('[data-mobile-menu-open]');
    var mobileCloseButtons = headerRoot.querySelectorAll('[data-mobile-menu-close]');
    var mobileOverlay = headerRoot.querySelector('[data-mobile-menu-overlay]');
    var mobileDrawer = headerRoot.querySelector('[data-mobile-menu-drawer]');
    var searchInput = headerRoot.querySelector('[data-desktop-search-input]');
    var searchPanel = headerRoot.querySelector('[data-desktop-search-panel]');
    var searchToggle = headerRoot.querySelector('[data-desktop-search-toggle]');
    var localeRoots = headerRoot.querySelectorAll('[data-locale-root]');

    setShellMeasurements();

    function closeSearchPanel() {
      if (!searchPanel) {
        return;
      }

      searchPanel.hidden = true;
      headerRoot.removeAttribute('data-search-open');
    }

    function openSearchPanel() {
      if (!searchPanel || !desktopHeaderBreakpoint.matches) {
        return;
      }

      searchPanel.hidden = false;
      headerRoot.setAttribute('data-search-open', 'true');
    }

    function closeLocaleMenus() {
      localeRoots.forEach(function (localeRoot) {
        var localeToggle = localeRoot.querySelector('[data-locale-toggle]');
        var localeMenu = localeRoot.querySelector('[data-locale-menu]');

        if (!localeToggle || !localeMenu) {
          return;
        }

        localeToggle.setAttribute('aria-expanded', 'false');
        localeMenu.hidden = true;
      });
    }

    function closeMobileDrawer() {
      if (!mobileDrawer || !mobileOverlay) {
        return;
      }

      document.body.classList.remove('tcghive-mobile-nav-open');
      mobileDrawer.classList.remove('is-open');
      mobileDrawer.setAttribute('hidden', 'hidden');
      mobileOverlay.setAttribute('hidden', 'hidden');
    }

    function openMobileDrawer() {
      if (!mobileDrawer || !mobileOverlay) {
        return;
      }

      document.body.classList.add('tcghive-mobile-nav-open');
      mobileDrawer.removeAttribute('hidden');
      mobileOverlay.removeAttribute('hidden');

      requestAnimationFrame(function () {
        mobileDrawer.classList.add('is-open');
      });
    }

    mobileOpenButtons.forEach(function (button) {
      button.addEventListener('click', openMobileDrawer);
    });

    mobileCloseButtons.forEach(function (button) {
      button.addEventListener('click', closeMobileDrawer);
    });

    if (mobileOverlay) {
      mobileOverlay.addEventListener('click', closeMobileDrawer);
    }

    if (searchInput) {
      searchInput.addEventListener('focus', openSearchPanel);
    }

    if (searchToggle) {
      searchToggle.addEventListener('click', function () {
        if (!searchPanel) {
          return;
        }

        if (searchPanel.hidden) {
          openSearchPanel();
        } else {
          closeSearchPanel();
        }
      });
    }

    localeRoots.forEach(function (localeRoot) {
      var localeToggle = localeRoot.querySelector('[data-locale-toggle]');
      var localeMenu = localeRoot.querySelector('[data-locale-menu]');
      var localizationInput = localeRoot.closest('form')
        ? localeRoot.closest('form').querySelector('[data-localization-input]')
        : null;
      var localeOptions = localeRoot.querySelectorAll('[data-locale-option]');

      if (!localeToggle || !localeMenu || !localizationInput) {
        return;
      }

      /*
        Keep Shopify's native localization form intact, but present it through
        a themed dropdown so the locale control matches the rest of the header.
      */
      localeToggle.addEventListener('click', function (event) {
        var isOpen;

        event.preventDefault();
        isOpen = localeToggle.getAttribute('aria-expanded') === 'true';

        closeLocaleMenus();

        if (!isOpen) {
          localeToggle.setAttribute('aria-expanded', 'true');
          localeMenu.hidden = false;
        }
      });

      localeOptions.forEach(function (optionButton) {
        optionButton.addEventListener('click', function () {
          var parentForm = localeRoot.closest('form');
          var nextLanguageCode = optionButton.getAttribute('data-language-code');

          if (!parentForm || !nextLanguageCode) {
            closeLocaleMenus();
            return;
          }

          if (localizationInput.value === nextLanguageCode) {
            closeLocaleMenus();
            return;
          }

          localizationInput.value = nextLanguageCode;
          parentForm.submit();
        });
      });
    });

    document.addEventListener('click', function (event) {
      if (!headerRoot.contains(event.target)) {
        closeSearchPanel();
        closeLocaleMenus();
        return;
      }

      if (!event.target.closest('[data-locale-root]')) {
        closeLocaleMenus();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeSearchPanel();
        closeMobileDrawer();
        closeLocaleMenus();
      }
    });

    window.addEventListener(
      'scroll',
      function () {
        closeSearchPanel();
        closeLocaleMenus();
      },
      { passive: true }
    );

    window.addEventListener('resize', function () {
      setShellMeasurements();

      if (!desktopHeaderBreakpoint.matches) {
        closeSearchPanel();
      }
    });

    window.addEventListener(
      'load',
      function () {
        setShellMeasurements();
      },
      { once: true }
    );

    [
      'shopify:section:load',
      'shopify:section:unload',
      'shopify:section:reorder',
      'shopify:section:select',
      'shopify:section:deselect'
    ].forEach(function (eventName) {
      document.addEventListener(eventName, scheduleShellMeasurement);
    });

    if ('ResizeObserver' in window) {
      var shellResizeObserver = new ResizeObserver(function () {
        setShellMeasurements();
      });

      shellResizeObserver.observe(headerRoot);

      var announcementBar = document.querySelector('.site-announcement');

      if (announcementBar) {
        shellResizeObserver.observe(announcementBar);
      }
    }
  }

  if (sharedCartToastCloseButton && window.TCGHiveCartFeedback) {
    sharedCartToastCloseButton.addEventListener('click', function () {
      window.TCGHiveCartFeedback.closeCartToast();
    });
  }

  initializeFaqAccordions(document);

  document.addEventListener('shopify:section:load', function (event) {
    initializeFaqAccordions(event.target);
  });

  document.addEventListener('submit', function (event) {
    var addForm = event.target.closest('[data-tcghive-cart-form]');
    var cartService = window.TCGHiveCartFeedback;
    var variantField;
    var quantityField;
    var submitButton;
    var quantity = 1;
    var maxQuantity = null;

    if (!addForm || !cartService) {
      return;
    }

    variantField = addForm.querySelector('input[name="id"]');
    submitButton = addForm.querySelector('button[type="submit"]');

    if (!variantField || !variantField.value) {
      return;
    }

    event.preventDefault();

    /*
      The shared homepage/browser cart flow must ignore repeated submits while
      one Ajax cart request is already in flight. This keeps double-clicks from
      creating duplicate cart lines or duplicate success toasts.
    */
    if (addForm.getAttribute('data-cart-pending') === 'true') {
      return;
    }

    quantityField = addForm.querySelector('input[name="quantity"]');
    quantity = quantityField && quantityField.value ? Number(quantityField.value) : 1;
    maxQuantity = Number(addForm.getAttribute('data-cart-max-quantity') || '');

    if (!quantity || quantity < 1) {
      quantity = 1;
    }

    if (!Number.isFinite(maxQuantity) || maxQuantity <= 0) {
      maxQuantity = null;
    } else {
      maxQuantity = Math.floor(maxQuantity);
    }

    addForm.setAttribute('data-cart-pending', 'true');

    if (submitButton) {
      submitButton.disabled = true;
    }

    cartService
      .addVariantToCart({
        variantId: variantField.value,
        quantity: quantity,
        maxQuantity: maxQuantity
      })
      .then(function () {
        cartService.showCartToast({
          type: 'success',
          productTitle: addForm.getAttribute('data-product-title') || '',
          variantSummary: addForm.getAttribute('data-variant-summary') || '',
          showActions: true
        });
      })
      .catch(function (error) {
        cartService.showCartToast({
          type: 'error',
          productTitle: addForm.getAttribute('data-product-title') || '',
          variantSummary: (error && (error.userMessage || error.message)) || '',
          showActions: false
        });
      })
      .finally(function () {
        addForm.removeAttribute('data-cart-pending');

        if (submitButton) {
          submitButton.disabled = false;
        }
      });
  });
});
